import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { useI18n } from "@/lib/i18n";
import { AUTH } from "@/constants/testIds";
import { toast } from "sonner";
import { Eye, EyeOff, Lock } from "lucide-react";
import { AltioraTreeLogo } from "@/components/brand/AltioraTreeLogo";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function goGoogle() {
  const redirectUrl = window.location.origin + "/app";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

export default function Login() {
  const { t } = useI18n();
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [busy, setBusy] = useState(false);
  const [emailError, setEmailError] = useState("");

  const validateEmail = (value) => {
    if (!value) return "";
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value) ? "" : "Format d'email invalide";
  };

  const submit = async (e) => {
    e.preventDefault();
    const err = validateEmail(email);
    setEmailError(err);
    if (err) { toast.error(err); return; }
    setBusy(true);
    try {
      const result = await login(email, password, rememberMe);
      // Route based on user's primary role (backend provides landing_route)
      const dest = result?.user?.landing_route || "/app";
      navigate(dest);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : "Identifiants invalides";
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthShell>
      <div className="text-[10px] font-semibold uppercase tracking-[0.32em]" style={{ color: "#C5A880" }}>
        ALTIORA · Intelligence décisionnelle
      </div>
      <h1 className="mt-4 font-serif font-medium text-4xl md:text-5xl tracking-tight text-[#0B2545] leading-[1.05]">
        {t("auth.signin") || "Se connecter"}
      </h1>
      <p className="mt-3 text-[14px] text-[#5B5B62] leading-relaxed">
        La première Intelligence Décisionnelle pour les professionnels.
      </p>

      <form onSubmit={submit} className="mt-10 space-y-5" noValidate>
        <Field label="Adresse courriel" error={emailError}>
          <input
            data-testid={AUTH.loginEmail}
            type="email" required autoComplete="email"
            value={email}
            onChange={(e) => { setEmail(e.target.value); setEmailError(""); }}
            onBlur={(e) => setEmailError(validateEmail(e.target.value))}
            className={`input-institutional ${emailError ? "border-[#991B1B] focus:border-[#991B1B] focus:ring-[#991B1B]/20" : ""}`}
          />
        </Field>
        <Field label="Mot de passe">
          <div className="relative">
            <input
              data-testid={AUTH.loginPassword}
              type={showPw ? "text" : "password"} required autoComplete="current-password"
              value={password} onChange={(e) => setPassword(e.target.value)}
              className="input-institutional pr-12"
            />
            <button
              type="button"
              onClick={() => setShowPw((v) => !v)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-[#6B7280] hover:text-navy p-1.5"
              data-testid={AUTH.loginPasswordToggle}
              aria-label={showPw ? "Masquer" : "Afficher"}
            >
              {showPw ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
            </button>
          </div>
        </Field>

        <div className="flex items-center justify-between text-sm">
          <label className="flex items-center gap-2 text-[#111827] cursor-pointer font-medium">
            <input
              type="checkbox"
              data-testid={AUTH.loginRememberMe}
              checked={rememberMe}
              onChange={(e) => setRememberMe(e.target.checked)}
              className="h-4 w-4 rounded border-[#D1D5DB] text-navy focus:ring-navy focus:ring-2"
            />
            <span>Se souvenir de moi</span>
          </label>
          <Link
            to="/forgot-password"
            data-testid={AUTH.loginForgotLink}
            className="text-navy hover:text-navy-700 font-semibold hover:underline underline-offset-2"
          >
            Mot de passe oublié&nbsp;?
          </Link>
        </div>

        <button
          data-testid={AUTH.loginSubmit}
          type="submit" disabled={busy}
          className="btn btn-lg btn-primary w-full"
        >
          <Lock className="h-4 w-4" strokeWidth={2.2} />
          {busy ? "Connexion…" : "Se connecter"}
        </button>
      </form>

      <Divider label="OU" />

      <div className="space-y-2">
        <button
          data-testid={AUTH.googleBtn}
          onClick={goGoogle}
          className="w-full rounded-md border border-[#E4E1D8] hover:border-[#B8B5AC] text-[#1F2937] py-2.5 text-sm font-medium transition-colors flex items-center justify-center gap-2"
        >
          <GoogleIcon /> Continuer avec Google
        </button>
        <div className="grid grid-cols-4 gap-2">
          <SocialPlaceholder testId={AUTH.msBtn} label="Microsoft" />
          <SocialPlaceholder testId={AUTH.appleBtn} label="Apple" />
          <SocialPlaceholder testId={AUTH.linkedinBtn} label="LinkedIn" />
          <SocialPlaceholder testId={AUTH.githubBtn} label="GitHub" />
        </div>
        <div className="text-[10px] text-[#8B8B8F] font-mono uppercase tracking-[0.15em] text-center">
          Autres fournisseurs — bientôt disponibles
        </div>
      </div>

      <p className="mt-8 text-sm text-[#5B5B62] text-center">
        Pas encore de compte&nbsp;?{" "}
        <Link to="/register" data-testid={AUTH.linkSignup} className="text-[#0B2545] hover:text-[#DCC29A] font-medium">
          Créer un compte
        </Link>
      </p>
    </AuthShell>
  );
}

function SocialPlaceholder({ testId, label }) {
  return (
    <button
      data-testid={testId}
      disabled
      title="Bientôt disponible"
      className="rounded-md border border-[#E4E1D8] bg-[#F8F7F3] text-[#8B8B8F] py-2 text-[10px] font-mono uppercase tracking-wider opacity-60 cursor-not-allowed"
    >
      {label}
    </button>
  );
}

export function AuthShell({ children }) {
  return (
    <div className="min-h-screen text-[#1F2937] grid grid-cols-1 lg:grid-cols-[1fr_460px]"
             style={{ background: "#FBF6EA" }}>
      {/* ── LEFT · Landing-Grade dark hero panel ────────────────── */}
      <div className="hidden lg:flex flex-col justify-between p-12 relative overflow-hidden"
              style={{ background: "#061528", color: "#FBF6EA" }}>
        {/* Radial glow (same as Landing hero) */}
        <div className="pointer-events-none absolute inset-0 opacity-40"
                style={{ background:
                  "radial-gradient(circle at 20% 30%, #C5A88033 0%, transparent 45%)," +
                  "radial-gradient(circle at 80% 70%, #0B254522 0%, transparent 50%)" }} />

        <Link to="/" className="relative flex items-center gap-4 group" data-testid="auth-brand">
          <span className="altiora-tree-hover inline-flex">
            <AltioraTreeLogo size={64} />
          </span>
          <div className="leading-tight">
            <div className="font-serif text-3xl tracking-tight" style={{ color: "#FBF6EA" }}>
              ALTIORA
            </div>
            <div className="mt-1 text-[10px] font-semibold uppercase tracking-[0.32em]"
                     style={{ color: "#C5A880" }}>
              Intelligence décisionnelle
            </div>
          </div>
        </Link>

        <div className="relative">
          <div className="text-[10px] uppercase tracking-[0.32em] font-semibold"
                  style={{ color: "#C5A880" }}>
            L'institution silencieuse
          </div>
          <div className="mt-4 font-serif tracking-tight leading-[1.05]"
                   style={{ fontSize: "clamp(2rem, 3.4vw, 3rem)" }}>
            Votre carrière,<br />
            <span style={{ color: "#F5D687", fontStyle: "italic" }}>
              orchestrée comme un système.
            </span>
          </div>
          <div className="mt-8 flex items-center gap-2 text-[10px] font-mono uppercase tracking-[0.24em]"
                  style={{ color: "#FBF6EA99" }}>
            <span className="h-1.5 w-1.5 rounded-full" style={{ background: "#7EE0AA" }} />
            Zero-Trust · Multi-modèles IA · Multi-tenant
          </div>
        </div>
      </div>

      {/* ── RIGHT · form panel on ivory ───────────────────────── */}
      <div className="flex items-center justify-center p-6 md:p-10 relative">
        <div className="w-full max-w-sm">{children}</div>
      </div>
    </div>
  );
}

export function Field({ label, error, children }) {
  return (
    <label className="block">
      <div className="mb-1.5 text-[11px] font-mono uppercase tracking-[0.2em] text-[#5B5B62]">{label}</div>
      {children}
      {error && <div className="mt-1 text-[11px] text-red-600">{error}</div>}
    </label>
  );
}

export function Divider({ label }) {
  return (
    <div className="my-6 flex items-center gap-3">
      <div className="h-px flex-1 bg-[#E4E1D8]" />
      <div className="text-[10px] font-mono uppercase tracking-widest text-[#5B5B62]">{label}</div>
      <div className="h-px flex-1 bg-[#E4E1D8]" />
    </div>
  );
}

export function GoogleIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
      <path fill="#EA4335" d="M12 10.2v3.9h5.5c-.2 1.5-1.7 4.4-5.5 4.4-3.3 0-6-2.7-6-6.1s2.7-6.1 6-6.1c1.9 0 3.1.8 3.8 1.4l2.6-2.5C16.7 3.6 14.6 2.7 12 2.7 6.9 2.7 2.8 6.8 2.8 12S6.9 21.3 12 21.3c6.9 0 9.4-4.8 9.4-9.5v-1.6H12z"/>
    </svg>
  );
}
